import React from 'react';
import { Link } from 'react-router-dom';
import { PageContainer } from '@/components/layout';

export const ExtensionGuide: React.FC = () => {
  return (
    <PageContainer maxWidth="full">
      <div className="text-center mb-8">
        <img 
          src="/ai_alt_text_generator_440x280.png" 
          alt="AI ALT Text Generator" 
          className="mx-auto rounded-lg shadow-md mb-4"
        />
        <h1 className="text-3xl font-bold text-gray-900 border-b-3 border-blue-500 pb-3 mb-4">
          AI ALT Text Generator - 확장 프로그램 사용 가이드
        </h1>
      </div>
      
      <p className="text-gray-700 mb-8 text-lg">
        AI ALT Text Generator는 웹페이지의 이미지에 대한 ALT 텍스트를 자동으로 생성하는 크롬 확장 프로그램입니다. 
        이 가이드를 통해 설치부터 사용까지 모든 단계를 쉽게 따라할 수 있습니다.
      </p>

      <div className="bg-gradient-to-r from-cyan-50 to-blue-50 border-l-4 border-cyan-500 p-5 rounded-r-lg mb-8">
        <h3 className="font-bold text-lg mb-3 text-gray-800">✨ 확장 프로그램 특징</h3>
        <ul className="space-y-2 text-gray-700">
          <li className="flex items-start gap-2">
            <span className="text-cyan-600 mt-1">✓</span>
            <span>모든 웹사이트에서 이미지 자동 분석</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-cyan-600 mt-1">✓</span>
            <span>AI 기반 고품질 ALT 텍스트 생성</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-cyan-600 mt-1">✓</span>
            <span>개인별 컨텍스트 프로필 지원</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-cyan-600 mt-1">✓</span>
            <span>실시간 미리보기 기능</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-cyan-600 mt-1">✓</span>
            <span>대시보드와 연동된 결과 관리</span>
          </li>
        </ul>
      </div>

      <div className="space-y-8">
        <section>
          <h2 className="text-2xl font-bold text-gray-800 border-b-2 border-gray-200 pb-2 mb-6">📦 1. 설치 방법</h2>
          
          <h3 className="text-xl font-semibold text-gray-700 mt-6 mb-4">Chrome Web Store에서 설치</h3>
          
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
            <ol className="space-y-4">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">1</span>
                <span className="text-gray-700">Chrome 브라우저에서 <a href="https://chromewebstore.google.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-semibold">Chrome Web Store</a> 접속</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">2</span>
                <span className="text-gray-700">검색창에 "AI ALT Text Generator" 검색</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">3</span>
                <span className="text-gray-700">"Chrome에 추가" 버튼 클릭</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">4</span>
                <span className="text-gray-700">권한 요청 팝업에서 "확장 프로그램 추가" 클릭</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">5</span>
                <span className="text-gray-700">설치 완료! 🎉</span>
              </li>
            </ol>
          </div>

          <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-r-lg">
            <p className="font-semibold text-green-800">💡 설치 확인</p>
            <p className="text-green-700 text-sm mt-1">설치가 완료되면 브라우저 우측 상단 퍼즐 아이콘을 클릭하여 "AI ALT Text Generator" 아이콘이 표시되는지 확인하세요.</p>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-800 border-b-2 border-gray-200 pb-2 mb-6">🔑 2. API 키 발급</h2>
          
          <p className="text-gray-700 mb-4">확장 프로그램을 사용하기 위해 먼저 API 키를 발급받아야 합니다.</p>
          
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
            <ol className="space-y-4">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">1</span>
                <span className="text-gray-700"><a href="https://alt.aigeneration-tech.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-semibold">alt.aigeneration-tech.com</a> 접속</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">2</span>
                <span className="text-gray-700">회원가입 또는 로그인</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">3</span>
                <span className="text-gray-700">대시보드에서 <Link to="/dashboard/ApiKeyManagement" className="text-blue-600 hover:underline font-semibold">"API 키 관리"</Link> 메뉴 선택</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">4</span>
                <span className="text-gray-700">"새 API 키 생성" 버튼 클릭</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">5</span>
                <span className="text-gray-700">API 키 이름 입력 (예: Chrome Extension)</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">6</span>
                <span className="text-gray-700">생성된 API 키 복사</span>
              </li>
            </ol>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-lg">
            <p className="font-semibold text-yellow-800">⚠️ 중요</p>
            <p className="text-yellow-700 text-sm mt-1">API 키는 안전하게 보관하세요. 유출되지 않도록 주의해야 합니다.</p>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-800 border-b-2 border-gray-200 pb-2 mb-6">⚙️ 3. 확장 프로그램 설정</h2>
          
          <h3 className="text-xl font-semibold text-gray-700 mt-6 mb-4">3.1 확장 프로그램 열기</h3>
          
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
            <ol className="space-y-4">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold">1</span>
                <span className="text-gray-700">Chrome 브라우저 우측 상단 퍼즐 아이콘 클릭</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold">2</span>
                <span className="text-gray-700">"AI ALT Text Generator" 아이콘 클릭</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold">3</span>
                <span className="text-gray-700">팝업이 열립니다</span>
              </li>
            </ol>
          </div>

          <div className="my-6">
            <img 
              src="/ai_alt_text_generator_view.png" 
              alt="AI ALT Text Generator 확장 프로그램 팝업" 
              className="w-full rounded-lg shadow-md border border-gray-200"
            />
            <p className="text-center text-sm text-gray-500 mt-2">확장 프로그램 팝업 화면</p>
          </div>

          <h3 className="text-xl font-semibold text-gray-700 mt-6 mb-4">3.2 API 키 입력</h3>
          
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
            <ol className="space-y-4">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold">1</span>
                <span className="text-gray-700">"API Key" 입력란에 발급받은 API 키 붙여넣기</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold">2</span>
                <span className="text-gray-700">"저장" 버튼 클릭</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold">3</span>
                <span className="text-gray-700">"API 키가 저장되었습니다." 알림 확인</span>
              </li>
            </ol>
          </div>

          <h3 className="text-xl font-semibold text-gray-700 mt-6 mb-4">3.3 컨텍스트 프로필 설정 (선택사항)</h3>
          
          <div className="bg-cyan-50 border-l-4 border-cyan-500 p-4 rounded-r-lg">
            <p className="font-semibold text-gray-800 mb-2">🎯 컨텍스트 프로필 사용</p>
            <ul className="space-y-1 text-gray-700 text-sm ml-4">
              <li>• "컨텍스트 프로필 사용" 체크박스 활성화</li>
              <li>• 개인별 설정에 맞는 ALT 텍스트가 생성됩니다</li>
              <li>• 체크 해제 시 일반적인 ALT 텍스트가 생성됩니다</li>
            </ul>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-800 border-b-2 border-gray-200 pb-2 mb-6">🚀 4. 사용 방법 (5단계)</h2>
          
          <h3 className="text-xl font-semibold text-gray-700 mt-6 mb-4">1단계: 페이지 정보 확인</h3>
          
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
            <ol className="space-y-4">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">1</span>
                <span className="text-gray-700">웹페이지 접속 후 확장 프로그램 팝업 열기</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">2</span>
                <span className="text-gray-700">"현재 페이지" 섹션에 URL과 제목이 표시됩니다</span>
              </li>
            </ol>
          </div>

          <div className="my-6 hidden">
            <img 
              src="/altgenerator1_resized_1280x800.jpg" 
              alt="페이지 정보 확인" 
              className="w-full rounded-lg shadow-md border border-gray-200"
            />
            <p className="text-center text-sm text-gray-500 mt-2">페이지 정보 확인 화면</p>
          </div>

          <h3 className="text-xl font-semibold text-gray-700 mt-6 mb-4">2단계: 이미지 분석</h3>
          
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
            <ol className="space-y-4">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">1</span>
                <span className="text-gray-700">"이미지 분석 시작" 버튼 클릭</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">2</span>
                <span className="text-gray-700">확장 프로그램이 페이지 내 모든 이미지를 분석</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">3</span>
                <span className="text-gray-700">분석 결과가 표시됩니다:</span>
              </li>
            </ol>
            <ul className="space-y-1 text-gray-600 text-sm ml-11 mt-2">
              <li>• 발견된 이미지: 총 이미지 수</li>
              <li>• ALT 없는 이미지: ALT 텍스트가 없는 이미지 수</li>
            </ul>
          </div>

          <div className="my-6 flex flex-col items-center">
            <img 
              src="/altgenerator2_resized_1280x800.jpg" 
              alt="이미지 분석 결과" 
              className="w-1/2 rounded-lg shadow-md border border-gray-200"
            />
            <p className="text-center text-sm text-gray-500 mt-2">이미지 분석 결과 화면</p>
          </div>

          <h3 className="text-xl font-semibold text-gray-700 mt-6 mb-4">3단계: ALT 텍스트 생성</h3>
          
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
            <ol className="space-y-4">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">1</span>
                <span className="text-gray-700">"ALT 텍스트 생성" 버튼 클릭</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">2</span>
                <span className="text-gray-700">진행률 바가 표시되며 생성 진행 상황 확인</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">3</span>
                <span className="text-gray-700">모든 ALT 텍스트 생성 완료 시 결과 표시</span>
              </li>
            </ol>
          </div>

          <div className="my-6 flex flex-col items-center">
            <img 
              src="/altgenerator3_resized_1280x800.jpg" 
              alt="ALT 텍스트 생성 완료" 
              className="w-1/2 rounded-lg shadow-md border border-gray-200"
            />
            <p className="text-center text-sm text-gray-500 mt-2">ALT 텍스트 생성 완료 화면</p>
          </div>

          <h3 className="text-xl font-semibold text-gray-700 mt-6 mb-4">4단계: 미리보기 적용</h3>
          
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
            <ol className="space-y-4">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">1</span>
                <span className="text-gray-700">"현재 화면에만 임시 적용" 버튼 클릭</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">2</span>
                <span className="text-gray-700">생성된 ALT 텍스트가 현재 화면에 적용됩니다</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">3</span>
                <span className="text-gray-700">적용된 이미지는 3초간 녹색 테두리로 표시됩니다</span>
              </li>
            </ol>
          </div>

          <div className="my-6 hidden">
            <img 
              src="/altgenerator1_1_resized_1280x800.jpg" 
              alt="미리보기 적용 화면" 
              className="w-full rounded-lg shadow-md border border-gray-200"
            />
            <p className="text-center text-sm text-gray-500 mt-2">미리보기 적용 화면</p>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-lg">
            <p className="font-semibold text-yellow-800">⚠️ 주의</p>
            <p className="text-yellow-700 text-sm mt-1">임시 적용은 현재 브라우저 세션에만 적용됩니다. 페이지를 새로고침하면 변경사항이 사라집니다.</p>
          </div>

          <h3 className="text-xl font-semibold text-gray-700 mt-6 mb-4">5단계: 실제 웹사이트에 적용</h3>
          
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
            <ol className="space-y-4">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">1</span>
                <span className="text-gray-700"><Link to="/dashboard/open-api-usage" className="text-blue-600 hover:underline font-semibold">대시보드에서 생성된 ALT 텍스트 확인</Link></span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">2</span>
                <span className="text-gray-700">CMS 또는 웹사이트 관리 도구에서 ALT 텍스트 적용</span>
              </li>
            </ol>
          </div>
        </section>

        <section className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">💡 팁</h2>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-1">•</span>
              <span>확장 프로그램은 모든 웹사이트에서 작동합니다</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-1">•</span>
              <span>컨텍스트 프로필을 사용하면 더 정확한 ALT 텍스트가 생성됩니다</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-1">•</span>
              <span>대시보드에서 이전 분석 결과를 확인할 수 있습니다</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-1">•</span>
              <span>문제가 발생하면 확장 프로그램을 다시 로드해 보세요</span>
            </li>
          </ul>
        </section>
      </div>
    </PageContainer>
  );
};
